import { Localization } from "@viamap/viamap2-common";
import type {
   LLMAnalysisResultItem,
   PaataleberettigetResolution,
   PaataleberettigetResolutionStatus,
} from "./ANNLLMAnalyzer";
import { normalizeAnnotationTypeValue, normalizeDatoloebenrValue } from "./ANNServitutDocumentAnnotations";
import {
   emptyProvenance,
   type ANNServitutProvenance,
} from "./ANNServitutProvenance";
import {
   AnnotationContextType,
   AnnotationType,
   type ANNAnnotationRecord,
   type AnnotationTypeCategory,
   type SaveAnnotationInput,
} from "./ANNTypes";

export const SERVITUT_ANALYSIS_FIELDS = ["resume", "entitledParty", "condition", "statement"] as const;
export type ServitutAnalysisFieldKey = typeof SERVITUT_ANALYSIS_FIELDS[number];

export const SERVITUT_FIELD_ANNOTATION_TYPE: Record<ServitutAnalysisFieldKey, AnnotationTypeCategory> = {
   resume: "Resumé",
   entitledParty: "Påtaleberettiget",
   condition: "Servituttype",
   statement: "Redegørelse",
};

export type ServitutFieldAnnotationMap = Partial<Record<ServitutAnalysisFieldKey, ANNAnnotationRecord>>;

function fieldKeyFromAnnotationType(value?: string): ServitutAnalysisFieldKey | null {
   const normalized = normalizeAnnotationTypeValue(value);
   if (normalized === "resumé" || normalized === "resume") {
      return "resume";
   }
   if (normalized === "påtaleberettiget") {
      return "entitledParty";
   }
   if (normalized === "rådighed/tilstand" || normalized === "rådighed" || normalized === "tilstand" || normalized === "servituttype") {
      return "condition";
   }
   if (normalized === "redegørelse") {
      return "statement";
   }
   return null;
}

export function isServitutAnalysisFieldNote(item: ANNAnnotationRecord, field?: ServitutAnalysisFieldKey): boolean {
   if (item.type !== AnnotationType.Note) {
      return false;
   }
   const key = fieldKeyFromAnnotationType(item.annotationType);
   if (!key) {
      return false;
   }
   return field ? key === field : true;
}

export function isFieldNoteAccepted(note?: ANNAnnotationRecord | null): boolean {
   return Boolean(note?.verified && (note.note?.text || "").trim());
}

export function listFieldNotesOfType(
   items: ANNAnnotationRecord[],
   field: ServitutAnalysisFieldKey,
   datoloebenr?: string,
): ANNAnnotationRecord[] {
   const target = normalizeDatoloebenrValue(datoloebenr);
   return items.filter((item) => {
      if (!isServitutAnalysisFieldNote(item, field)) {
         return false;
      }
      if (target && normalizeDatoloebenrValue(item.datoloebenr) !== target) {
         return false;
      }
      return true;
   });
}

export function collectFieldNotes(
   items: ANNAnnotationRecord[],
   datoloebenr?: string,
): ServitutFieldAnnotationMap {
   const map: ServitutFieldAnnotationMap = {};
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      const notes = listFieldNotesOfType(items, field, datoloebenr);
      if (notes.length) {
         map[field] = notes.find((note) => note.verified) || notes[0];
      }
   });
   return map;
}

export function overlayAcceptedFieldStatuses(
   provenance: ANNServitutProvenance | undefined,
   fieldAnnotations?: ServitutFieldAnnotationMap,
): ANNServitutProvenance {
   const fieldStatus = { ...(provenance?.fieldStatus || {}) };
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      if (isFieldNoteAccepted(fieldAnnotations?.[field])) {
         fieldStatus[field] = "person_approved";
      } else if (fieldStatus[field] === "person_approved") {
         delete fieldStatus[field];
      }
   });
   return {
      ...emptyProvenance(),
      ...provenance,
      fieldStatus,
   };
}

export function analysisFieldLabel(field: ServitutAnalysisFieldKey): string {
   switch (field) {
      case "resume":
         return Localization.getText("Resume");
      case "entitledParty":
         return Localization.getText("Entitled party");
      case "condition":
         return Localization.getText("Easement type");
      case "statement":
         return Localization.getText("Statement");
   }
}

function getAcceptedFieldText(note?: ANNAnnotationRecord | null): string {
   return (note?.note?.text || "").trim();
}

function formatServitutTypeLabel(value?: string): string {
   if (!value) {
      return "";
   }
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") {
      return Localization.getText("Easement of use");
   }
   if (normalized === "tilstandsservitut") {
      return Localization.getText("Condition easement");
   }
   return value;
}

function formatPaataleStatus(status?: PaataleberettigetResolutionStatus): string {
   switch (status) {
      case "likely":
         return Localization.getText("Likely suggestion");
      case "ambiguous":
         return Localization.getText("Several possible successors");
      case "not_found":
         return Localization.getText("Current entitled party AI could not find");
      case "error":
         return Localization.getText("Current entitled party AI could not find");
      default:
         return "";
   }
}

export function getSavedPaataleberettigetResolution(
   note?: ANNAnnotationRecord | null,
): PaataleberettigetResolution | undefined {
   return note?.note?.paataleberettigetResolution;
}

export function hydrateAnalysisItemFromPaataleNote(
   analysisItem: LLMAnalysisResultItem | undefined,
   note?: ANNAnnotationRecord | null,
): LLMAnalysisResultItem | undefined {
   const saved = getSavedPaataleberettigetResolution(note);
   if (!saved) {
      return analysisItem;
   }
   if (analysisItem?.paataleberettiget_resolution) {
      return analysisItem;
   }
   return {
      aktId: analysisItem?.aktId ?? null,
      processing_method: analysisItem?.processing_method || "saved_annotation",
      reasoning: analysisItem?.reasoning || "",
      pages_kept: analysisItem?.pages_kept || [],
      original_pages: analysisItem?.original_pages ?? 0,
      extracted_pages: analysisItem?.extracted_pages ?? 0,
      ...analysisItem,
      paataleberettiget: analysisItem?.paataleberettiget || saved.historical_name,
      paataleberettiget_resolution: saved,
   };
}

function formatPaataleberettigetArgumentation(resolution?: PaataleberettigetResolution): string {
   if (!resolution) {
      return "";
   }
   const parts: string[] = [];
   const statusLabel = formatPaataleStatus(resolution.status);
   if (resolution.suggested_current_name && statusLabel) {
      parts.push(statusLabel);
   }
   if (resolution.reasoning?.trim()) {
      parts.push(resolution.reasoning.trim());
   }
   if (resolution.successor_chain?.length) {
      const chainLines = resolution.successor_chain.map((step, index) => {
         const lines = [`${index + 1}. ${step.from} → ${step.to}`];
         if (step.relationship?.trim()) {
            lines.push(`   ${step.relationship.trim()}`);
         }
         if (step.effective_date?.trim()) {
            lines.push(`   ${Localization.getText("Effective from")}: ${step.effective_date.trim()}`);
         }
         if (step.evidence?.trim()) {
            lines.push(`   ${step.evidence.trim()}`);
         }
         return lines.join("\n");
      });
      parts.push(`${Localization.getText("Successor chain")}:\n${chainLines.join("\n")}`);
   }
   if (resolution.alternatives?.length) {
      const alternativeLines = resolution.alternatives.map((alternative) => (
         alternative.reasoning?.trim()
            ? `- ${alternative.name}: ${alternative.reasoning.trim()}`
            : `- ${alternative.name}`
      ));
      parts.push(`${Localization.getText("Other possible entitled parties")}:\n${alternativeLines.join("\n")}`);
   }
   if (resolution.sources?.length) {
      const sourceLines = resolution.sources.map((source) => {
         const title = source.title?.trim();
         const url = source.url?.trim();
         if (title && url && title !== url) {
            return `- ${title}: ${url}`;
         }
         return `- ${title || url}`;
      });
      parts.push(`${Localization.getText("Sources")}:\n${sourceLines.join("\n")}`);
   }
   return parts.filter(Boolean).join("\n\n");
}

export function formatCaseRelevantLabel(value?: boolean): string {
   if (value === true) {
      return Localization.getText("Relevant");
   }
   if (value === false) {
      return Localization.getText("Not relevant");
   }
   return "";
}

export function getAiFieldText(
   field: ServitutAnalysisFieldKey,
   analysisItem: LLMAnalysisResultItem | undefined,
   options?: { caseDescription?: string; tingbogSummary?: string },
): string {
   return buildAiFieldText(field, analysisItem, options);
}

function buildAiFieldText(
   field: ServitutAnalysisFieldKey,
   analysisItem: LLMAnalysisResultItem | undefined,
   options?: { caseDescription?: string; tingbogSummary?: string },
): string {
   if (field === "resume") {
      return (analysisItem?.summary || "").trim();
   }
   if (field === "condition") {
      return formatServitutTypeLabel(analysisItem?.servitut_type);
   }
   if (field === "entitledParty") {
      const resolution = analysisItem?.paataleberettiget_resolution;
      const currentLine = resolution?.suggested_current_name
         ? `${Localization.getText("AI suggested current")}: ${resolution.suggested_current_name}`
         : resolution
            ? `${Localization.getText("Current")}: ${
               formatPaataleStatus(resolution.status)
               || Localization.getText("Current entitled party AI could not find")
            }`
            : "";
      const historicalName = analysisItem?.paataleberettiget || resolution?.historical_name;
      const historicalLine = historicalName
         ? `${Localization.getText("Historical")}: ${historicalName}`
         : "";
      return [
         currentLine,
         historicalLine,
         formatPaataleberettigetArgumentation(resolution),
      ].filter(Boolean).join("\n\n");
   }
   const stampedCaseDescription = analysisItem?.case_description?.trim();
   const statement = stampedCaseDescription
      ? `${Localization.getText("Problemstilling")}: ${stampedCaseDescription}`
      : "";
   const relevance = formatCaseRelevantLabel(analysisItem?.case_relevant);
   const relevanceLine = relevance
      ? `${Localization.getText("AI answer")}: ${relevance}`
      : "";
   const aiResult = analysisItem?.case_relevance_reasoning?.trim() || "";
   const header = [statement, relevanceLine].filter(Boolean).join("\n");
   return [header, aiResult].filter(Boolean).join("\n\n");
}

export function getRowFieldText(
   row: {
      analysisItem?: LLMAnalysisResultItem;
      summary?: string;
      fieldAnnotations?: ServitutFieldAnnotationMap;
      provenance?: { analysisStale?: boolean };
   },
   field: ServitutAnalysisFieldKey,
   caseDescription?: string,
): string {
   const accepted = getAcceptedFieldText(row.fieldAnnotations?.[field]);
   if (isFieldNoteAccepted(row.fieldAnnotations?.[field]) && accepted) {
      return accepted;
   }
   if (row.provenance?.analysisStale && !(field === "entitledParty" && row.analysisItem?.paataleberettiget_resolution)) {
      return "";
   }
   return buildAiFieldText(field, row.analysisItem, {
      caseDescription,
      tingbogSummary: row.summary,
   });
}

export function isRowFieldAccepted(
   row: { fieldAnnotations?: ServitutFieldAnnotationMap },
   field: ServitutAnalysisFieldKey,
): boolean {
   return isFieldNoteAccepted(row.fieldAnnotations?.[field]);
}

export function buildFieldNoteSaveInput(params: {
   field: ServitutAnalysisFieldKey;
   documentId: string;
   datoLoebenr?: string;
   organizationId: string;
   userId: string;
   userName: string;
   text: string;
   verified: boolean;
   existing?: ANNAnnotationRecord;
   quality?: SaveAnnotationInput["quality"];
   historyComment?: string;
   paataleberettigetResolution?: PaataleberettigetResolution;
}): SaveAnnotationInput {
   const annotationType = SERVITUT_FIELD_ANNOTATION_TYPE[params.field];
   const title = [
      Localization.getText(annotationType),
      params.datoLoebenr,
   ].filter(Boolean).join(" · ");
   return {
      type: AnnotationType.Note,
      contextType: params.existing?.contextType || AnnotationContextType.Servitut,
      documentId: params.documentId,
      datoloebenr: params.existing?.datoloebenr || params.datoLoebenr,
      organizationId: params.organizationId,
      userId: params.userId,
      userName: params.userName,
      annotationType,
      title,
      note: {
         comment: title,
         text: params.text.trim(),
         ...(params.paataleberettigetResolution
            ? { paataleberettigetResolution: params.paataleberettigetResolution }
            : {}),
      },
      quality: params.quality,
      verified: params.verified,
      historyComment: params.historyComment,
      id: params.existing?.id,
      originalCreatedAt: params.existing?.createdAt,
      originalCreatedBy: params.existing?.createdBy,
      originalCreatedByName: params.existing?.createdByName,
   };
}

export async function saveUniqueFieldNote(params: {
   field: ServitutAnalysisFieldKey;
   documentId: string;
   datoLoebenr?: string;
   organizationId: string;
   userId: string;
   userName: string;
   text: string;
   existing?: ANNAnnotationRecord;
   siblings?: ANNAnnotationRecord[];
   verified?: boolean;
   quality?: SaveAnnotationInput["quality"];
   historyComment?: string;
   paataleberettigetResolution?: PaataleberettigetResolution;
   saveAnnotation: (input: SaveAnnotationInput) => Promise<ANNAnnotationRecord>;
   deleteAnnotation: (id: string, organizationId?: string) => Promise<void>;
}): Promise<ANNAnnotationRecord> {
   const siblings = params.siblings?.length
      ? params.siblings
      : listFieldNotesOfType(params.existing ? [params.existing] : [], params.field, params.datoLoebenr);
   const keep = params.existing
      || siblings.find((note) => note.verified)
      || siblings[0];
   const record = await params.saveAnnotation(buildFieldNoteSaveInput({
      field: params.field,
      documentId: params.documentId,
      datoLoebenr: params.datoLoebenr,
      organizationId: params.organizationId,
      userId: params.userId,
      userName: params.userName,
      text: params.text,
      quality: params.quality,
      verified: params.verified !== false,
      existing: keep,
      historyComment: params.historyComment,
      paataleberettigetResolution: params.paataleberettigetResolution
         || (params.field === "entitledParty" ? keep?.note?.paataleberettigetResolution : undefined),
   }));
   for (const extra of siblings) {
      if (!extra.id || extra.id === record.id) {
         continue;
      }
      try {
         await params.deleteAnnotation(extra.id, params.organizationId);
      } catch (error) {
         console.warn("[Servitutredegørelse] failed to delete duplicate field annotation", extra.id, error);
      }
   }
   return record;
}
